import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import ParticleSystem from './ParticleSystem';
import CosmicRings from './CosmicRings';
import NebulaField from './NebulaField';
import SpectrumAnalyzer from './SpectrumAnalyzer';

const AudioVisualizer = ({ audioData, vibeConfig }) => {
  const coreRef = useRef();
  const outerSphereRef = useRef();
  
  const { colors, geometry, effects } = vibeConfig || {};

  // Animation loop
  useFrame((state) => {
    if (!audioData) return;

    const time = state.clock.getElapsedTime();
    const animSpeed = geometry?.animationSpeed || 1.5;
    
    // Core sphere animation - smooth and cosmic
    if (coreRef.current) {
      if (effects?.rotation) {
        coreRef.current.rotation.x = time * 0.3 * animSpeed;
        coreRef.current.rotation.y = time * 0.2 * animSpeed;
        coreRef.current.rotation.z = time * 0.1 * animSpeed;
      }
      
      if (effects?.pulsing) {
        const scale = 1 + audioData.average * 3 + audioData.bass * 2;
        coreRef.current.scale.setScalar(scale);
      }
      
      // Subtle floating motion
      coreRef.current.position.y = Math.sin(time * 0.5) * 0.3 + Math.sin(time * 2) * audioData.mid * 0.8;
      coreRef.current.position.x = Math.cos(time * 0.3) * 0.2 + Math.cos(time * 1.8) * audioData.treble * 0.6;
    }

    // Outer energy sphere
    if (outerSphereRef.current) {
      outerSphereRef.current.rotation.x = -time * 0.2;
      outerSphereRef.current.rotation.y = time * 0.4;
      const outerScale = 1.5 + audioData.peak * 1.5 + audioData.treble * 2;
      outerSphereRef.current.scale.setScalar(outerScale);
    }
  });

  const primaryColor = colors?.primary || '#00ffff';
  const secondaryColor = colors?.secondary || '#ff0080';
  const accentColor = colors?.accent || '#8000ff';

  return (
    <group>
      {/* Nebula background field */}
      <NebulaField audioData={audioData} vibeConfig={vibeConfig} />
      
      {/* Colorful spectrum analyzer */}
      <SpectrumAnalyzer audioData={audioData} vibeConfig={vibeConfig} />
      
      {/* Particle systems */}
      <ParticleSystem audioData={audioData} vibeConfig={vibeConfig} />
      
      {/* Cosmic ring system */}
      <CosmicRings audioData={audioData} vibeConfig={vibeConfig} />
      
      {/* Core energy sphere */}
      <mesh ref={coreRef}>
        <sphereGeometry args={[1.2, 64, 64]} />
        <meshStandardMaterial 
          color={primaryColor}
          transparent
          opacity={0.8}
          emissive={primaryColor}
          emissiveIntensity={audioData ? 0.3 + audioData.average * 0.7 : 0.3}
          roughness={0.1}
          metalness={0.9}
        />
      </mesh>
      
      {/* Outer energy field */}
      <mesh ref={outerSphereRef}>
        <sphereGeometry args={[2.5, 32, 32]} />
        <meshStandardMaterial 
          color={secondaryColor}
          transparent
          opacity={audioData ? 0.15 + audioData.treble * 0.25 : 0.15}
          emissive={secondaryColor}
          emissiveIntensity={audioData ? 0.1 + audioData.treble * 0.4 : 0.1}
          wireframe
        />
      </mesh>

      {/* Inner core glow */}
      <mesh>
        <sphereGeometry args={[0.8, 32, 32]} />
        <meshStandardMaterial 
          color={accentColor}
          transparent
          opacity={audioData ? 0.6 + audioData.bass * 0.4 : 0.6}
          emissive={accentColor}
          emissiveIntensity={audioData ? 0.5 + audioData.bass * 0.5 : 0.5}
        />
      </mesh>

      {/* Energy tendrils */}
      {audioData && Array.from({ length: 12 }, (_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const radius = 4 + audioData.treble * 3;
        return (
          <mesh 
            key={i}
            position={[
              Math.cos(angle) * radius,
              Math.sin(angle) * radius * 0.5,
              Math.sin(angle * 2) * 2
            ]}
            rotation={[angle, angle * 0.5, 0]}
            scale={[0.05, 1 + audioData.treble * 3, 0.05]}
          >
            <cylinderGeometry args={[1, 0.1, 1, 8]} />
            <meshStandardMaterial 
              color={colors?.highlight || '#ffff00'}
              transparent
              opacity={0.7 + audioData.treble * 0.3}
              emissive={colors?.highlight || '#ffff00'}
              emissiveIntensity={0.8}
            />
          </mesh>
        );
      })}
    </group>
  );
};

export default AudioVisualizer;